import type { Document as RichTextDocument } from '@contentful/rich-text-types';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useRollbar } from '@rollbar/react';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

import Button, {
  ButtonIconPosition,
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import LeadFormContainer from '@/components/Form/LeadFormContainer/LeadFormContainer';
import { IconName } from '@/components/Icon/Icon';
import GridContainer2 from '@/components/Layout/GridContainer2/GridContainer2';
import type { ButtonContent } from '@/components/OldButton/OldButton';
import type {
  ComponentSys,
  Contentful,
  FrontifyImageContent,
} from '@/contentful/types';
import {
  type LeadFormInputData,
  useSubmitLeadToZeus,
} from '@/hooks/useSubmitLeadToZeus';
import { trackEvents } from '@/utils/analytics';
import { getErrorMessage, getErrorObject } from '@/utils/getErrorMessage';
import {
  emailValidator,
  fullNameValidator,
  honeypotAddressFieldValidator,
  lastBillValidator,
  OwnHome,
  ownHomeValidator,
  phoneValidator,
  zipCodeValidator,
} from '@/utils/validation/inputs';

import useStyles from './PaidLander.styles';
import ConfirmationStep from './Steps/ConfirmationStep';
import QualifyStep from './Steps/QualifyStep';
import SubmitStep from './Steps/SubmitStep';

export const FORM_SUBMIT_ERROR_MESSAGE =
  'There was an error submitting the form, please try again';

export interface PaidLanderContent extends Contentful {
  sys: ComponentSys;
  fields: {
    firstStepHeader?: string;
    firstStepSubHeading?: string;
    firstStepImage?: FrontifyImageContent[];
    firstStepImageAltText?: string;
    nextStepText?: string;
    secondStepHeader?: string;
    secondStepSubHeading?: string;
    secondStepImage?: FrontifyImageContent[];
    secondStepImageAltText?: string;
    submitFormText?: string;
    successScreenHeader?: string;
    successScreenImage?: FrontifyImageContent[];
    successScreenImageAltText?: string;
    successScreenDescription?: RichTextDocument;
    failureScreenHeader: string;
    failureScreenImage?: FrontifyImageContent[];
    failureScreenImageAltText?: string;
    failureScreenDescription?: RichTextDocument;
    failureCta?: ButtonContent;
    showHomeownerQuestion?: boolean;
    isOnDark?: boolean;
    fixedGrid?: boolean;
    tcpa?: RichTextDocument;
    zipCodeLabel?: string;
    averageElectricBillLabel?: string;
    averageElectricBillPlaceholder?: string;
    ownYourHomeLabel?: string;
    fullNameLabel?: string;
    emailLabel?: string;
    phoneNumberLabel?: string;
    reverseLayout?: boolean;
    campaignId?: string;
    overrideCampaignId: boolean;
    hideImageOnMobile?: boolean;
  };
}

export interface PaidLanderProps {
  content: PaidLanderContent;
  showQualify: boolean;
  showSubmit: boolean;
  placeholder?: string;
}

export const landerQualifyStepSchema = z.object({
  ...zipCodeValidator,
  ...ownHomeValidator,
  ...lastBillValidator,
});

export const landerSubmitStepSchema = z.object({
  ...fullNameValidator,
  ...emailValidator,
  ...phoneValidator,
  ...honeypotAddressFieldValidator,
});

export const landerSchema = landerQualifyStepSchema.merge(
  landerSubmitStepSchema
);

export type LanderQualifyStepSchemaType = z.infer<
  typeof landerQualifyStepSchema
>;
export type LanderSubmitStepSchemaType = z.infer<typeof landerSubmitStepSchema>;
export type LanderSchemaType = z.infer<typeof landerSchema>;

const scrollElementIntoView = (element: HTMLElement | null) => {
  const globalNav = document.getElementById('global-nav');
  if (!element) return;

  const elRect = element.getBoundingClientRect();
  // calculating element start position relative to document
  const elementTop = window.scrollY + elRect.top;

  if (globalNav) {
    const navRect = globalNav.getBoundingClientRect();

    // window scrolls up, navbar gets hidden
    if (elRect.top >= 0) {
      window.scrollTo({ top: elementTop, behavior: 'smooth' });
    } else {
      // window scrolls down, navbar always shows up
      window.scrollTo({
        top: elementTop - navRect.height,
        behavior: 'smooth',
      });
    }
  } else {
    // page uses minimal or no header
    window.scrollTo({ top: elementTop, behavior: 'smooth' });
  }
};

export default function PaidLander({
  content: {
    fields: {
      firstStepHeader,
      firstStepSubHeading,
      firstStepImage,
      firstStepImageAltText,
      nextStepText,
      secondStepHeader,
      secondStepSubHeading,
      secondStepImage,
      secondStepImageAltText,
      submitFormText,
      successScreenHeader,
      successScreenImage,
      successScreenImageAltText,
      successScreenDescription,
      failureScreenHeader,
      failureScreenImage,
      failureScreenImageAltText,
      failureScreenDescription,
      failureCta,
      showHomeownerQuestion,
      isOnDark = false,
      fixedGrid = false,
      tcpa,
      zipCodeLabel = 'What is your zip code?',
      averageElectricBillLabel = 'What is your average electric bill?',
      averageElectricBillPlaceholder = 'e.g. $500',
      ownYourHomeLabel = 'Do you own your home?',
      fullNameLabel = 'Full name',
      emailLabel = 'Email',
      phoneNumberLabel = 'Phone number',
      reverseLayout = false,
      campaignId,
      overrideCampaignId = false,
      hideImageOnMobile = false,
    },
  },
  showQualify = true,
  showSubmit = false,
}: PaidLanderProps) {
  const { setError } = useForm<LanderSchemaType>();
  const { classes, cx } = useStyles({ isOnDark });
  const { submitLeadToZeus } = useSubmitLeadToZeus();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFormComplete, setIsFormComplete] = useState<boolean>(false);
  const [formData, setFormData] = useState<LanderSchemaType>({
    fullName: '',
    email: '',
    zipCode: '',
    phone: '',
    ownHome: OwnHome.OWN,
    lastBill: '',
  });
  const [showQualifyStep, setShowQualifyStep] = useState<boolean>(showQualify);
  const [showSubmitStep, setShowSubmitStep] = useState<boolean>(showSubmit);
  const [showSuccess, setSuccess] = useState<boolean>(false);
  const [showFailure, setFailure] = useState<boolean>(false);
  const rollbar = useRollbar();

  const handleNext = (data: LanderQualifyStepSchemaType) => {
    trackEvents({
      event: 'Form_cta_click',
      Form_tab: 'Zip Code/Electrical Bill/Home Ownership',
      Form_step: 1,
      click_text: 'Next',
    });
    setShowQualifyStep(false);
    if (data.ownHome === OwnHome.OWN) {
      setFormData({ ...formData, ...data });
      setShowSubmitStep(true);
    } else {
      setFailure(true);
    }
    scrollElementIntoView(containerRef.current);
  };

  const handleBack = () => {
    setShowQualifyStep(true);
    setShowSubmitStep(false);
    setFailure(false);
  };

  const handleSubmitLanderForm = (data: LanderSubmitStepSchemaType) => {
    trackEvents({
      event: 'Form_cta_click',
      Form_tab: 'First Name/Last Name/Email/Phone',
      Form_step: 2,
      click_text: 'Submit',
    });
    setFormData((prevData) => ({ ...prevData, ...data }));
    setIsFormComplete(true);
    setShowSubmitStep(true);
    scrollElementIntoView(containerRef.current);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Automated migration to Biome
  useEffect(() => {
    if (isFormComplete) {
      setShowSubmitStep(false);
      if (showHomeownerQuestion && formData.ownHome === OwnHome.RENT) {
        setFailure(true);
      } else {
        setSuccess(true);
      }
      onSubmit(formData);
    }
  }, [isFormComplete, formData]);

  const onSubmit: SubmitHandler<LanderSchemaType> = async (
    data: LanderSchemaType
  ) => {
    const { fullName, email, zipCode, phone, lastBill, ownHome, address } =
      data;

    const nameParts = fullName.split(' ');
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ');

    try {
      const zeusLeadFormData: LeadFormInputData = {
        first_name: firstName,
        last_name: lastName,
        email: email,
        zip: zipCode,
        phone_home: phone,
        electric_bill: lastBill,
        property_ownership: showHomeownerQuestion
          ? (ownHome as OwnHome)
          : undefined,
      };
      await submitLeadToZeus({
        zeusData: zeusLeadFormData,
        contentfulCampaignId: campaignId,
        gtmSubmitEventFormType: 'Interactive Quote Form',
        overrideCampaignId,
        honeypot: Boolean(address),
      });
    } catch (err: unknown) {
      rollbar.error(getErrorMessage(err), getErrorObject(err));
      setError('root.formSubmitError', {
        type: 'formSubmit',
        message: FORM_SUBMIT_ERROR_MESSAGE,
      });
      return;
    }
  };

  return (
    <div
      className={cx(classes.container, {
        [classes.fullHeightContainer]: !fixedGrid,
      })}
      ref={containerRef}
    >
      <GridContainer2 fixed={fixedGrid}>
        <Grid2>
          <Button
            iconName={IconName.ARROW_LEFT}
            iconPosition={ButtonIconPosition.LEFT}
            variant={ButtonVariant.TERTIARY}
            size={ButtonSize.SM}
            label='Previous step'
            onClick={handleBack}
            isOnDark={isOnDark}
            className={classNames({
              [classes.hiddenBackButton]:
                showQualifyStep || showSuccess || showFailure,
            })}
          />
        </Grid2>
      </GridContainer2>
      <div className={classes.content}>
        <LeadFormContainer>
          <QualifyStep
            heading={firstStepHeader}
            subheading={firstStepSubHeading}
            hideImageOnMobile={hideImageOnMobile}
            // biome-ignore lint/complexity/useOptionalChain: Automated migration to Biome
            image={firstStepImage && firstStepImage[0]?.preview_url}
            altText={firstStepImageAltText}
            submitText={nextStepText}
            showHomeownerQuestion={showHomeownerQuestion}
            isOnDark={isOnDark}
            nextPage={handleNext}
            show={showQualifyStep}
            zipCodeLabel={zipCodeLabel}
            averageElectricBillLabel={averageElectricBillLabel}
            averageElectricBillPlaceholder={averageElectricBillPlaceholder}
            ownYourHomeLabel={ownYourHomeLabel}
            reverseLayout={reverseLayout}
            fixedGrid={fixedGrid}
          />
          <SubmitStep
            heading={secondStepHeader}
            subheading={secondStepSubHeading}
            hideImageOnMobile={hideImageOnMobile}
            // biome-ignore lint/complexity/useOptionalChain: Automated migration to Biome
            image={secondStepImage && secondStepImage[0]?.preview_url}
            altText={secondStepImageAltText}
            submitText={submitFormText}
            isOnDark={isOnDark}
            submit={handleSubmitLanderForm}
            show={showSubmitStep}
            tcpa={tcpa}
            fullNameLabel={fullNameLabel}
            emailLabel={emailLabel}
            phoneNumberLabel={phoneNumberLabel}
            reverseLayout={reverseLayout}
            fixedGrid={fixedGrid}
          />
        </LeadFormContainer>
        <ConfirmationStep
          heading={successScreenHeader}
          description={successScreenDescription}
          hideImageOnMobile={hideImageOnMobile}
          // biome-ignore lint/complexity/useOptionalChain: Automated migration to Biome
          imageSrc={successScreenImage && successScreenImage[0]?.preview_url}
          imageAltText={successScreenImageAltText}
          buttonContent={undefined}
          isOnDark={isOnDark}
          show={showSuccess}
          reverseLayout={reverseLayout}
          fixedGrid={fixedGrid}
        />
        <ConfirmationStep
          heading={failureScreenHeader}
          description={failureScreenDescription}
          hideImageOnMobile={hideImageOnMobile}
          // biome-ignore lint/complexity/useOptionalChain: Automated migration to Biome
          imageSrc={failureScreenImage && failureScreenImage[0]?.preview_url}
          imageAltText={failureScreenImageAltText}
          buttonContent={failureCta}
          isOnDark={isOnDark}
          show={showFailure}
          reverseLayout={reverseLayout}
          fixedGrid={fixedGrid}
        />
      </div>
    </div>
  );
}
